import { useEffect, useState, useCallback } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import _GridLayout from 'react-grid-layout'
const GridLayout = _GridLayout as any
import {
  ArrowLeft, Plus, Settings2, Trash2, Save, Edit3, X, GripVertical, AlertTriangle
} from 'lucide-react'
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts'
import api from '../../services/api'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

// ── Types ─────────────────────────────────────────────────────────────────────

type WidgetType = 'kpi_card' | 'line_chart' | 'bar_chart' | 'grouped_bar' | 'pie_chart' | 'table'

interface Widget {
  i: string
  x: number; y: number; w: number; h: number
  type: WidgetType
  title: string
  metric: string
  period: string
  color?: string
  filters?: { site_id?: string; contractor_id?: string }
}

// ── Metric catalogue ──────────────────────────────────────────────────────────

const METRICS: Record<WidgetType, { value: string; label: string; group: string }[]> = {
  kpi_card: [
    { value: 'trifr',                    label: 'TRIFR',                      group: 'Safety Rates' },
    { value: 'ltifr',                    label: 'LTIFR',                      group: 'Safety Rates' },
    { value: 'days_since_lti',           label: 'Days Since Last LTI',        group: 'Safety Rates' },
    { value: 'audit_pass_rate',          label: 'Audit Pass Rate',            group: 'Safety Rates' },
    { value: 'incident_count',           label: 'Total Incidents',            group: 'Incidents' },
    { value: 'near_miss_count',          label: 'Near Misses',                group: 'Incidents' },
    { value: 'notifiable_count',         label: 'Notifiable Incidents',       group: 'Incidents' },
    { value: 'lost_time_injuries',       label: 'Lost Time Injuries',         group: 'Incidents' },
    { value: 'total_hours_worked',       label: 'Total Hours Worked',         group: 'Operations' },
    { value: 'active_permits_count',     label: 'Active Permits',             group: 'Operations' },
    { value: 'active_contractors_count', label: 'Active Contractors',         group: 'Operations' },
    { value: 'audit_count',              label: 'Audits Completed',           group: 'Operations' },
    { value: 'avg_audit_score',          label: 'Avg Audit Score',            group: 'Operations' },
    { value: 'open_actions_count',       label: 'Open Actions',               group: 'Actions' },
    { value: 'overdue_actions_count',    label: 'Overdue Actions',            group: 'Actions' },
    { value: 'ca_completion_rate',       label: 'Action Completion Rate',     group: 'Actions' },
    { value: 'wc_claims_count',          label: "Open Workers' Comp Claims",  group: "Workers' Comp" },
    { value: 'wc_total_cost',            label: "Workers' Comp Total Cost",   group: "Workers' Comp" },
  ],
  line_chart: [
    { value: 'incident_trend',             label: 'Incident Trend',                group: 'Incidents' },
    { value: 'near_miss_trend',            label: 'Near Miss Trend',               group: 'Incidents' },
    { value: 'incidents_by_month_type',    label: 'Incidents by Type (Monthly)',   group: 'Incidents' },
    { value: 'incident_yoy',              label: 'Incidents Year-on-Year',         group: 'Comparisons' },
    { value: 'trifr_trend',              label: 'TRIFR Trend',                    group: 'Safety Rates' },
    { value: 'audit_score_trend',         label: 'Audit Score Trend',             group: 'Audits' },
    { value: 'audit_score_trend_monthly', label: 'Audit Score Monthly',           group: 'Audits' },
    { value: 'audit_score_yoy',           label: 'Audit Score Year-on-Year',      group: 'Comparisons' },
    { value: 'action_closure_trend',      label: 'Action Closure Trend',          group: 'Actions' },
    { value: 'wc_cost_trend',             label: "Workers' Comp Cost Trend",      group: "Workers' Comp" },
    { value: 'permit_trend',              label: 'Permit Trend',                  group: 'Permits' },
  ],
  bar_chart: [
    { value: 'incidents_by_category',       label: 'Incidents by Category',        group: 'Incidents' },
    { value: 'incidents_by_severity',       label: 'Incidents by Severity',        group: 'Incidents' },
    { value: 'incidents_by_site',           label: 'Incidents by Site',            group: 'Incidents' },
    { value: 'incidents_by_contractor',     label: 'Incidents by Contractor',      group: 'Incidents' },
    { value: 'audits_by_type',              label: 'Audits by Type',               group: 'Audits' },
    { value: 'audit_scores_by_site',        label: 'Audit Scores by Site',         group: 'Audits' },
    { value: 'audit_scores_by_contractor',  label: 'Audit Scores by Contractor',   group: 'Audits' },
    { value: 'actions_by_status',           label: 'Actions by Status',            group: 'Actions' },
    { value: 'actions_by_priority',         label: 'Actions by Priority',          group: 'Actions' },
    { value: 'actions_by_owner',            label: 'Open Actions by Owner',        group: 'Actions' },
    { value: 'permits_by_type',             label: 'Permits by Type',              group: 'Permits' },
    { value: 'wc_cost_by_site',             label: "Workers' Comp Cost by Site",   group: "Workers' Comp" },
  ],
  grouped_bar: [
    { value: 'contractor_audit_comparison',    label: 'Contractor Audit Comparison',    group: 'Contractors' },
    { value: 'contractor_incident_comparison', label: 'Contractor Incident Comparison', group: 'Contractors' },
  ],
  pie_chart: [
    { value: 'incidents_by_category', label: 'Incidents by Category', group: 'Incidents' },
    { value: 'incidents_by_severity', label: 'Incidents by Severity', group: 'Incidents' },
    { value: 'audits_by_type',        label: 'Audits by Type',        group: 'Audits' },
    { value: 'actions_by_status',     label: 'Actions by Status',     group: 'Actions' },
    { value: 'actions_by_priority',   label: 'Actions by Priority',   group: 'Actions' },
    { value: 'permits_by_type',       label: 'Permits by Type',       group: 'Permits' },
  ],
  table: [
    { value: 'recent_incidents',            label: 'Recent Incidents',              group: 'Incidents' },
    { value: 'top_incident_sites',          label: 'Top Incident Sites',            group: 'Incidents' },
    { value: 'overdue_actions',             label: 'Overdue Actions',               group: 'Actions' },
    { value: 'expiring_competencies',       label: 'Expiring Competencies',         group: 'Training' },
    { value: 'contractor_summary',          label: 'Contractor Summary',            group: 'Contractors' },
    { value: 'contractor_prequalification', label: 'Contractor Prequalification',   group: 'Contractors' },
    { value: 'wc_claims_detail',            label: "Workers' Comp Claims",          group: "Workers' Comp" },
  ],
}

const PERIODS = [
  { value: '7d',  label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '6m',  label: 'Last 6 months' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'ytd', label: 'Year to date' },
]

const TYPE_LABELS: Record<WidgetType, string> = {
  kpi_card: 'KPI Card', line_chart: 'Line Chart', bar_chart: 'Bar Chart',
  grouped_bar: 'Grouped Bar', pie_chart: 'Pie Chart', table: 'Table',
}

const TEAL = '#00C4A1'
const CHART_COLORS = [TEAL, '#2D7DD2', '#F5921E', '#6554C0', '#D93025', '#3DB54A', '#8B5CF6', '#EC4899']
const YOY_COLORS = [TEAL, '#94A3B8']
const MULTI_COLORS = ['#D93025', '#F5921E', TEAL, '#2D7DD2', '#8B5CF6', '#64748B']

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtKPI(value: number | string, suffix?: string, prefix?: string): string {
  const n = typeof value === 'number' ? value : parseFloat(String(value)) || 0
  if (prefix === '$') {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`
    return `$${n.toFixed(0)}`
  }
  const formatted = n >= 1_000 ? `${(n / 1_000).toFixed(0)}k` : String(value)
  return `${prefix || ''}${formatted}${suffix || ''}`
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: '#0D1B3E', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
      {label && <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 4, fontSize: 11 }}>{label}</p>}
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color || '#fff', fontWeight: 600 }}>{p.name ? `${p.name}: ` : ''}{p.value ?? '—'}</p>
      ))}
    </div>
  )
}

// ── Widget data hook ──────────────────────────────────────────────────────────

function useWidgetData(metric: string, period: string, filters?: Widget['filters']) {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const filtersKey = JSON.stringify(filters || {})

  useEffect(() => {
    setLoading(true); setError('')
    api.post('/analytics/widget-data', { metric, period, filters: filters || {} })
      .then(r => setData(r.data))
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false))
  }, [metric, period, filtersKey])

  return { data, loading, error }
}

// ── Widget renderers ──────────────────────────────────────────────────────────

function KPICard({ widget }: { widget: Widget }) {
  const { data, loading, error } = useWidgetData(widget.metric, widget.period, widget.filters)
  const accent = widget.color || 'var(--accent)'

  if (loading) return <div className="flex items-center justify-center h-full" style={{ color: 'var(--text-muted)' }}>…</div>
  if (error) return <div className="text-xs text-center py-4" style={{ color: 'var(--red)' }}>{error}</div>

  const val = data?.value ?? 0
  const isAlert = data?.alert

  return (
    <div className="flex flex-col justify-center h-full px-2">
      <p className="text-4xl font-extrabold leading-none" style={{ color: isAlert ? 'var(--red)' : accent }}>
        {fmtKPI(val, data?.suffix, data?.prefix)}
      </p>
      <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
        {PERIODS.find(p => p.value === widget.period)?.label}
      </p>
      {isAlert && (
        <div className="flex items-center gap-1 mt-1.5">
          <AlertTriangle size={12} style={{ color: 'var(--red)' }} />
          <span className="text-xs font-medium" style={{ color: 'var(--red)' }}>Requires attention</span>
        </div>
      )}
    </div>
  )
}

function LineChartWidget({ widget }: { widget: Widget }) {
  const { data, loading, error } = useWidgetData(widget.metric, widget.period, widget.filters)
  if (loading) return <div className="flex items-center justify-center h-full" style={{ color: 'var(--text-muted)' }}>…</div>
  if (error || !data?.rows?.length) return <div className="text-xs text-center py-8" style={{ color: 'var(--text-muted)' }}>No data</div>

  const isYoY = data.type === 'timeseries_yoy'
  const isMulti = data.type === 'timeseries_multi'
  const series: string[] = isYoY ? ['current', 'previous'] : isMulti ? (data.series || []) : ['value']
  const colors = isYoY ? YOY_COLORS : MULTI_COLORS

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data.rows} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
        <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
        <Tooltip content={<CustomTooltip />} />
        {(isYoY || isMulti) && <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />}
        {series.map((s, i) => (
          <Line
            key={s}
            type="monotone"
            dataKey={s}
            stroke={colors[i % colors.length]}
            strokeWidth={isYoY && s === 'previous' ? 1.5 : 2}
            strokeDasharray={isYoY && s === 'previous' ? '4 2' : undefined}
            dot={false}
            name={isYoY ? (s === 'current' ? 'This Year' : 'Last Year') : s.replace(/_/g, ' ')}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}

function BarChartWidget({ widget }: { widget: Widget }) {
  const { data, loading, error } = useWidgetData(widget.metric, widget.period, widget.filters)
  if (loading) return <div className="flex items-center justify-center h-full" style={{ color: 'var(--text-muted)' }}>…</div>
  if (error || !data?.rows?.length) return <div className="text-xs text-center py-8" style={{ color: 'var(--text-muted)' }}>No data</div>

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data.rows} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="value" fill={widget.color || TEAL} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function GroupedBarChartWidget({ widget }: { widget: Widget }) {
  const { data, loading, error } = useWidgetData(widget.metric, widget.period, widget.filters)
  if (loading) return <div className="flex items-center justify-center h-full" style={{ color: 'var(--text-muted)' }}>…</div>
  if (error || !data?.rows?.length) return <div className="text-xs text-center py-8" style={{ color: 'var(--text-muted)' }}>No data</div>

  const series: string[] = data.series || []

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data.rows} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 80 }}>
        <XAxis type="number" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#94A3B8' }} width={80} axisLine={false} tickLine={false} />
        <Tooltip content={<CustomTooltip />} />
        <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
        {series.map((s, i) => (
          <Bar key={s} dataKey={s} fill={CHART_COLORS[i % CHART_COLORS.length]}
            radius={[0, 3, 3, 0]} name={s.replace(/_/g, ' ')} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  )
}

function PieChartWidget({ widget }: { widget: Widget }) {
  const { data, loading, error } = useWidgetData(widget.metric, widget.period, widget.filters)
  if (loading) return <div className="flex items-center justify-center h-full" style={{ color: 'var(--text-muted)' }}>…</div>
  if (error || !data?.rows?.length) return <div className="text-xs text-center py-8" style={{ color: 'var(--text-muted)' }}>No data</div>

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data.rows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius="70%" innerRadius="35%">
          {data.rows.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend iconSize={10} wrapperStyle={{ fontSize: 11, color: '#64748B' }} />
      </PieChart>
    </ResponsiveContainer>
  )
}

function TableWidget({ widget }: { widget: Widget }) {
  const { data, loading, error } = useWidgetData(widget.metric, widget.period, widget.filters)
  if (loading) return <div className="flex items-center justify-center h-full" style={{ color: 'var(--text-muted)' }}>…</div>
  if (error) return <div className="text-xs text-center py-4" style={{ color: 'var(--red)' }}>{error}</div>
  if (!data?.rows?.length) return <div className="text-xs text-center py-8" style={{ color: 'var(--text-muted)' }}>No data</div>

  const cols: string[] = data.columns || []

  return (
    <div className="overflow-auto h-full">
      <table className="w-full text-xs">
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {cols.map(c => (
              <th key={c} className="text-left py-1.5 px-2 font-semibold capitalize" style={{ color: 'var(--text-muted)', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {c.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row: any, i: number) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              {cols.map(c => (
                <td key={c} className="py-1.5 px-2" style={{ color: 'var(--text)' }}>{row[c] ?? '—'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function WidgetRenderer({ widget }: { widget: Widget }) {
  switch (widget.type) {
    case 'kpi_card':    return <KPICard widget={widget} />
    case 'line_chart':  return <LineChartWidget widget={widget} />
    case 'bar_chart':   return <BarChartWidget widget={widget} />
    case 'grouped_bar': return <GroupedBarChartWidget widget={widget} />
    case 'pie_chart':   return <PieChartWidget widget={widget} />
    case 'table':       return <TableWidget widget={widget} />
    default:            return null
  }
}

// ── Widget config panel ───────────────────────────────────────────────────────

const DEFAULT_SIZES: Record<WidgetType, { w: number; h: number }> = {
  kpi_card:    { w: 3, h: 2 },
  line_chart:  { w: 6, h: 3 },
  bar_chart:   { w: 6, h: 3 },
  grouped_bar: { w: 6, h: 4 },
  pie_chart:   { w: 4, h: 3 },
  table:       { w: 6, h: 4 },
}

interface ConfigPanelProps {
  initial?: Partial<Widget>
  onSave: (w: Partial<Widget>) => void
  onClose: () => void
}

function ConfigPanel({ initial, onSave, onClose }: ConfigPanelProps) {
  const [type, setType] = useState<WidgetType>(initial?.type || 'kpi_card')
  const [metric, setMetric] = useState(initial?.metric || METRICS.kpi_card[0].value)
  const [title, setTitle] = useState(initial?.title || '')
  const [period, setPeriod] = useState(initial?.period || '30d')
  const [color, setColor] = useState(initial?.color || CHART_COLORS[0])
  const [filterSiteId, setFilterSiteId] = useState(initial?.filters?.site_id || '')
  const [filterContractorId, setFilterContractorId] = useState(initial?.filters?.contractor_id || '')
  const [filterOptions, setFilterOptions] = useState<{ sites: any[]; contractors: any[] }>({ sites: [], contractors: [] })

  useEffect(() => {
    api.get('/analytics/filter-options').then(r => setFilterOptions(r.data)).catch(() => {})
  }, [])

  const metricsForType = METRICS[type]

  const groupedMetrics = metricsForType.reduce((acc, m) => {
    if (!acc[m.group]) acc[m.group] = []
    acc[m.group].push(m)
    return acc
  }, {} as Record<string, typeof metricsForType>)

  const handleTypeChange = (t: WidgetType) => {
    setType(t)
    setMetric(METRICS[t][0].value)
  }

  const save = () => {
    const filters: Widget['filters'] = {}
    if (filterSiteId) filters.site_id = filterSiteId
    if (filterContractorId) filters.contractor_id = filterContractorId
    onSave({
      type,
      metric,
      title: title || metricsForType.find(m => m.value === metric)?.label || metric,
      period,
      color,
      filters: Object.keys(filters).length ? filters : undefined,
      ...DEFAULT_SIZES[type],
    })
  }

  return (
    <div className="fixed inset-y-0 right-0 w-80 shadow-xl z-50 flex flex-col" style={{ background: 'var(--white)', borderLeft: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
        <h3 className="font-semibold" style={{ color: 'var(--text)' }}>{initial?.i ? 'Edit Widget' : 'Add Widget'}</h3>
        <button onClick={onClose} style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Widget Type */}
        <div>
          <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Widget Type</label>
          <div className="grid grid-cols-2 gap-2">
            {(['kpi_card','line_chart','bar_chart','grouped_bar','pie_chart','table'] as WidgetType[]).map(t => (
              <button
                key={t}
                onClick={() => handleTypeChange(t)}
                className="py-2 px-3 rounded-lg text-xs font-medium border transition-all text-left"
                style={{
                  background: type === t ? 'var(--accent)' : 'var(--white)',
                  color: type === t ? 'white' : 'var(--text-secondary)',
                  borderColor: type === t ? 'var(--accent)' : 'var(--border)',
                }}
              >
                {TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>

        {/* Metric */}
        <div>
          <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Metric</label>
          <select value={metric} onChange={e => setMetric(e.target.value)}>
            {Object.entries(groupedMetrics).map(([group, items]) => (
              <optgroup key={group} label={group}>
                {items.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Title */}
        <div>
          <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Title (optional)</label>
          <input
            placeholder={metricsForType.find(m => m.value === metric)?.label || ''}
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </div>

        {/* Period */}
        {type !== 'table' && (
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Period</label>
            <select value={period} onChange={e => setPeriod(e.target.value)}>
              {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        )}

        {/* Filters */}
        {(filterOptions.sites.length > 0 || filterOptions.contractors.length > 0) && (
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Filters</label>
            <div className="space-y-2">
              {filterOptions.sites.length > 0 && (
                <select value={filterSiteId} onChange={e => setFilterSiteId(e.target.value)}>
                  <option value="">All sites</option>
                  {filterOptions.sites.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
              {filterOptions.contractors.length > 0 && (
                <select value={filterContractorId} onChange={e => setFilterContractorId(e.target.value)}>
                  <option value="">All contractors</option>
                  {filterOptions.contractors.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </div>
          </div>
        )}

        {/* Colour */}
        {type !== 'table' && type !== 'pie_chart' && (
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--text-secondary)' }}>Colour</label>
            <div className="flex gap-2 flex-wrap">
              {CHART_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className="w-7 h-7 rounded-full border-2 transition-all"
                  style={{ background: c, borderColor: color === c ? 'var(--text)' : 'transparent' }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="p-4 border-t flex gap-2" style={{ borderColor: 'var(--border)' }}>
        <button onClick={save} className="btn btn-primary flex-1">
          {initial?.i ? 'Update' : 'Add Widget'}
        </button>
        <button onClick={onClose} className="btn btn-secondary">Cancel</button>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AnalyticsDashboard() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [dashboard, setDashboard] = useState<any>(null)
  const [widgets, setWidgets] = useState<Widget[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [configTarget, setConfigTarget] = useState<string | 'new' | null>(null)
  const [containerWidth, setContainerWidth] = useState(900)

  useEffect(() => {
    const el = document.querySelector('main')
    if (el) setContainerWidth(el.clientWidth - 48)
    const obs = new ResizeObserver(entries => setContainerWidth(entries[0].contentRect.width - 48))
    if (el) obs.observe(el)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    api.get(`/analytics/dashboards/${id}`)
      .then(({ data }) => {
        setDashboard(data)
        setWidgets(Array.isArray(data.layout) ? data.layout : [])
      })
      .catch(() => navigate('/analytics'))
      .finally(() => setLoading(false))
  }, [id])

  const save = async (newWidgets?: Widget[]) => {
    const toSave = newWidgets ?? widgets
    setSaving(true)
    try { await api.patch(`/analytics/dashboards/${id}`, { layout: toSave }) } catch {}
    setSaving(false)
  }

  const onLayoutChange = useCallback((layout: any[]) => {
    if (!editing) return
    setWidgets(prev => prev.map(w => {
      const pos = layout.find((l: any) => l.i === w.i)
      return pos ? { ...w, x: pos.x, y: pos.y, w: pos.w, h: pos.h } : w
    }))
  }, [editing])

  const addOrUpdateWidget = (cfg: Partial<Widget>) => {
    setWidgets(prev => {
      const existing = prev.find(w => w.i === configTarget)
      if (existing) return prev.map(w => w.i === configTarget ? { ...w, ...cfg } : w)
      const maxY = prev.reduce((m, w) => Math.max(m, w.y + w.h), 0)
      const newWidget: Widget = {
        i: `w-${Date.now()}`,
        x: 0, y: maxY,
        w: cfg.w ?? 4, h: cfg.h ?? 2,
        type: cfg.type!,
        title: cfg.title!,
        metric: cfg.metric!,
        period: cfg.period || '30d',
        color: cfg.color,
        filters: cfg.filters,
      }
      return [...prev, newWidget]
    })
    setConfigTarget(null)
  }

  const removeWidget = (i: string) => setWidgets(prev => prev.filter(w => w.i !== i))

  const configWidget = configTarget === 'new' ? undefined : widgets.find(w => w.i === configTarget)

  if (loading) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading…</div>

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/analytics" style={{ color: 'var(--text-muted)' }}><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>{dashboard?.name}</h1>
            {dashboard?.description && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{dashboard.description}</p>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {editing && (
            <>
              <button onClick={() => setConfigTarget('new')} className="btn btn-secondary flex items-center gap-1.5 text-sm">
                <Plus size={14} /> Add Widget
              </button>
              <button onClick={async () => { await save(); setEditing(false) }} disabled={saving} className="btn btn-primary flex items-center gap-1.5 text-sm">
                <Save size={14} /> {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)} className="btn btn-secondary text-sm">Cancel</button>
            </>
          )}
          {!editing && (
            <button onClick={() => setEditing(true)} className="btn btn-secondary flex items-center gap-1.5 text-sm">
              <Edit3 size={14} /> Edit
            </button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {widgets.length === 0 && (
        <div className="card p-12 text-center space-y-3">
          <p className="font-medium" style={{ color: 'var(--text)' }}>No widgets yet</p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Click Edit → Add Widget to start building your dashboard</p>
          <button onClick={() => { setEditing(true); setConfigTarget('new') }} className="btn btn-primary mx-auto flex items-center gap-2">
            <Plus size={15} /> Add First Widget
          </button>
        </div>
      )}

      {/* Grid */}
      {widgets.length > 0 && (
        <div style={{ position: 'relative' }}>
          <GridLayout
            className="layout"
            layout={widgets.map(w => ({ i: w.i, x: w.x, y: w.y, w: w.w, h: w.h, minW: 2, minH: 2 }))}
            cols={12}
            rowHeight={80}
            width={containerWidth}
            isDraggable={editing}
            isResizable={editing}
            onLayoutChange={onLayoutChange as any}
            draggableHandle=".drag-handle"
            margin={[12, 12]}
          >
            {widgets.map(widget => (
              <div key={widget.i} className="card overflow-hidden flex flex-col" style={{ padding: 0, borderTop: `2px solid ${widget.color || TEAL}` }}>
                <div className="flex items-center justify-between px-3 py-2 flex-shrink-0" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                  <div className="flex items-center gap-2 min-w-0">
                    {editing && <GripVertical size={14} className="drag-handle cursor-grab flex-shrink-0" style={{ color: 'var(--text-muted)' }} />}
                    <span className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>{widget.title}</span>
                    {widget.filters?.site_id || widget.filters?.contractor_id ? (
                      <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: 'var(--accent-light)', color: 'var(--accent)', fontSize: 10 }}>filtered</span>
                    ) : null}
                  </div>
                  {editing && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => setConfigTarget(widget.i)} className="p-1 rounded hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}>
                        <Settings2 size={13} />
                      </button>
                      <button onClick={() => removeWidget(widget.i)} className="p-1 rounded hover:bg-red-50" style={{ color: 'var(--red)' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex-1 p-3 min-h-0">
                  <WidgetRenderer widget={widget} />
                </div>
              </div>
            ))}
          </GridLayout>
        </div>
      )}

      {/* Config panel */}
      {configTarget !== null && (
        <ConfigPanel
          initial={configWidget}
          onSave={addOrUpdateWidget}
          onClose={() => setConfigTarget(null)}
        />
      )}
    </div>
  )
}
